import React from "react";
import { Button } from "../Button/Button.jsx";

/**
 * EmptyState — Placeholder shown when a list, table, or panel has no content.
 *
 * Token-only styling (see styles/components/_empty-state.scss). Pass a rendered
 * icon node (e.g. <Icons name="search" size={48} />) rather than a name string,
 * so any icon or illustration can be used.
 *
 * @param {node}   icon            - Rendered icon/illustration displayed above the title.
 * @param {node}   title           - Primary message or semantic heading (required).
 * @param {node}   description     - Optional supporting text below the title.
 * @param {object} primaryAction   - { label, onClick, isLoading, disabled, Type, Size } → Button.
 * @param {object} secondaryAction - { label, onClick, disabled, Type, Size } → Button.
 * @param {string} size            - "s" | "m" (default "m"); controls vertical padding.
 * @param {string} className       - Extra class names for the container.
 */
export const EmptyState = ({
  icon = null,
  title,
  description = null,
  primaryAction = null,
  secondaryAction = null,
  size = "m",
  className = "",
}) => {
  if (!title) {
    return null;
  }

  const containerClasses = `ds-empty-state ds-empty-state--${size} ${className}`.trim();

  return (
    <div className={containerClasses}>
      {icon && <div className="ds-empty-state__icon">{icon}</div>}
      <div className="ds-empty-state__title body-l-semibold">{title}</div>
      {description && (
        <div className="ds-empty-state__description body-m">{description}</div>
      )}
      {(primaryAction || secondaryAction) && (
        <div className="ds-empty-state__actions">
          {secondaryAction && (
            <Button
              Type={secondaryAction.Type || "Secondary"}
              Size={secondaryAction.Size}
              onClick={secondaryAction.onClick}
              disabled={secondaryAction.disabled}
            >
              {secondaryAction.label}
            </Button>
          )}
          {primaryAction && (
            <Button
              Type={primaryAction.Type || "Primary"}
              Size={primaryAction.Size}
              onClick={primaryAction.onClick}
              isLoading={primaryAction.isLoading}
              disabled={primaryAction.disabled}
            >
              {primaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default EmptyState;
